import { Formik, Form, Field } from 'formik';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useCrowdGallery, { CrowdGallerySettings } from './hooks/useCrowdGallery';
import LoadingIndicator from '../../Common/components/LoadingIndicator';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/shadComponents/ui/label';
import { Textarea } from '@/src/shadComponents/ui/textarea';

export default function EditCrowdGallery() {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [settings, setSettings] = useState<CrowdGallerySettings | null>(null);
    const { getCrowdGallery, saveCrowdGallery } = useCrowdGallery();
    const { galleryId: id } = useParams();
    const galleryId = id ? +id : 0;

    useEffect(() => {
        let isMounted = true;
        const load = async () => {
            try {
                const settings: CrowdGallerySettings = await getCrowdGallery(
                    galleryId,
                );

                isMounted && setSettings(settings);
            } catch (error) {
                console.error(error);
            } finally {
                isMounted && setIsLoading(false);
            }
        };

        load();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isLoading || !settings) {
        return <LoadingIndicator />;
    }

    return (
        <div className="max-w-3xl mx-auto py-8 px-4">
            <div className="flex items-center gap-2 mb-6">
                <h1 className="text-2xl font-bold tracking-tight text-foreground">Edit Crowd Gallery Settings</h1>
            </div>
            <Formik
                initialValues={settings}
                onSubmit={async (values, actions) => {
                    const wasSuccessful = await saveCrowdGallery(
                        settings.id,
                        values,
                    );
                    actions.setSubmitting(false);

                    if (wasSuccessful) {
                        navigate(-1);
                    }
                }}
            >
                {(props) => (
                    <Form
                        className="space-y-6"
                        placeholder={undefined}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                    >
                        <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                                <Field
                                    id="enabled"
                                    name="enabled"
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-border"
                                />
                                <Label htmlFor="enabled">Enabled</Label>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="keyword">Short Code</Label>
                            <Field
                                as={Input}
                                id="keyword"
                                name="keyword"
                                placeholder="Short code"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="welcomeMessage">Welcome Message</Label>
                            <Field
                                as={Textarea}
                                id="welcomeMessage"
                                name="welcomeMessage"
                                rows={4}
                                placeholder="Hey you, Smilebooth here! Text your photos to be added to the gallery. Txt and data rates may apply."
                            />
                        </div>

                        <h2 className="text-lg font-semibold tracking-tight pt-4">Response Settings</h2>

                        <div className="space-y-2">
                            <Label htmlFor="responseMessage">Message</Label>
                            <Field
                                as={Textarea}
                                id="responseMessage"
                                name="responseMessage"
                                rows={4}
                            />
                        </div>

                        <div className="flex items-center gap-6">
                            <div className="flex items-center space-x-2">
                                <Field
                                    id="sendPhoto"
                                    name="sendPhoto"
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-border"
                                />
                                <Label htmlFor="sendPhoto">Send photo</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Field
                                    id="sendPersonalGalleryLink"
                                    name="sendPersonalGalleryLink"
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-border"
                                />
                                <Label htmlFor="sendPersonalGalleryLink">
                                    Send personal gallery link
                                </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Field
                                    id="sendGalleryLink"
                                    name="sendGalleryLink"
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-border"
                                />
                                <Label htmlFor="sendGalleryLink">
                                    Send gallery link
                                </Label>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 pt-6 border-t border-border">
                            <Button type="submit">
                                Save Settings
                            </Button>
                            <Button
                                type="button"
                                onClick={() => navigate(-1)}
                                variant="outline"
                            >
                                Cancel
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
}
